import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <motion.div
      className="min-h-screen bg-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button
          variant="ghost"
          className="mb-6 text-amber-500 hover:text-amber-400 hover:bg-amber-900/20"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-gray-900/90 border-2 border-amber-900/30 rounded-lg p-6 md:p-10">
          <h1 className="text-3xl font-headline text-amber-500 tracking-wider mb-2">TERMS OF SERVICE</h1>
          <p className="text-gray-500 font-typewriter text-sm mb-8">Last updated: October 2026</p>

          <div className="space-y-6 text-gray-300 font-typewriter text-sm leading-relaxed">

            <section className="bg-amber-900/10 border border-amber-700/30 rounded-lg p-5">
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">CONTENT DISCLAIMER</h2>
              <p>
                All content on Deadline by Daria, including but not limited to text, images, videos, characters, storylines, 
                and scenarios, is <strong className="text-amber-400">entirely fictional</strong> and created solely for 
                <strong className="text-amber-400"> educational and entertainment purposes</strong>.
              </p>
              <p className="mt-3">
                Deadline by Daria does not condone, promote, or encourage violence, harm, or illegal activity against any 
                individual or group. Any resemblance to real persons, living or dead, or actual events is purely coincidental.
              </p>
              <p className="mt-3">
                The creators and operators of this platform bear no responsibility for the misinterpretation of fictional content. 
                All materials should be understood within the context of a fictional detective thriller narrative.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">1. AGE REQUIREMENT</h2>
              <p>
                You must be at least 18 years of age to access and use this platform. By creating an account or 
                accessing any content, you confirm that you are 18 years of age or older. We reserve the right to 
                terminate accounts of users who do not meet this age requirement.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">2. ACCEPTANCE OF TERMS</h2>
              <p>
                By accessing or using Deadline by Daria, you agree to be bound by these Terms of Service. If you do not 
                agree to these terms, you must not access or use the platform.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">3. ACCOUNT RESPONSIBILITIES</h2>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials and for all activities 
                that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">4. SUBSCRIPTIONS & PAYMENTS</h2>
              <p className="mb-3" data-testid="terms-billing-renewal">
                Paid memberships are processed securely through Stripe or PayPal. All prices are displayed in USD.
                Memberships renew automatically each month at the listed price until you cancel. You can cancel anytime
                from your account page, and you keep access until the end of the period you've paid for.
              </p>
              <p className="mb-3">
                Items in the Store (individual photosets, films or collections) are single, one-time purchases and do not renew.
              </p>
              <p className="mb-3" data-testid="terms-refund-policy">
                <span className="text-amber-400">Refunds.</span> Because content is digital and delivered immediately, payments
                are non-refundable, except in cases of billing errors or duplicate charges, which we'll correct within
                5 business days. Contact <a href="mailto:deadlinebydaria@gmail.com" className="text-amber-500 hover:text-amber-400 underline">deadlinebydaria@gmail.com</a>.
              </p>
              <p data-testid="terms-statement-descriptor">
                <span className="text-amber-400">Your statement.</span> Charges appear on your card statement as{' '}
                <span className="text-gray-100 font-semibold">DEADLINE BY DARIA</span>. PayPal payments appear as a PayPal charge to Deadline by Daria.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">5. INTELLECTUAL PROPERTY</h2>
              <p>
                All content, including original stories, artwork, videos, and other creative materials, is the intellectual 
                property of Deadline by Daria and its creator. You may not reproduce, distribute, or create derivative 
                works from any content without explicit written permission.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">6. USER CONDUCT</h2>
              <p>You agree not to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>Share, redistribute, or leak paid content</li>
                <li>Harass, threaten, or abuse other members or the creator</li>
                <li>Use the platform for any illegal purpose</li>
                <li>Impersonate other users or the creator</li>
                <li>Attempt to access content without proper tier authorization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">7. TERMINATION</h2>
              <p>
                We reserve the right to suspend or terminate your account at our discretion, particularly for violations 
                of these terms. Upon termination, your right to access paid content will cease.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">8. LIMITATION OF LIABILITY</h2>
              <p>
                Deadline by Daria is provided "as is" without warranties of any kind. We shall not be liable for any 
                indirect, incidental, or consequential damages arising from your use of the platform.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">9. CHANGES TO TERMS</h2>
              <p>
                We may update these Terms of Service at any time. Continued use of the platform after changes constitutes 
                acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">10. CONTACT</h2>
              <p>
                For questions about these Terms of Service, please contact us through the platform's messaging system 
                or via the contact information provided on our website.
              </p>
            </section>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TermsOfService;
